import { CoreField } from 'bloko/common/tree/types';

import { ProfRoles } from 'store/autoReducers';

export const PROFROLE_ID_DELIMITER = ';';

export interface TreeProfRoles<T extends string | number = string | number> {
    [CoreField.Id]: T;
    [CoreField.Text]: string;
    items?: TreeProfRoles<number>[];
}

export interface BEProfRoles {
    professionalCategories: ProfRoles[];
}

export const prepareProfRolesOnLoad = (items: TreeProfRoles<number>[]): TreeProfRoles<string>[] =>
    items.map(({ id, text, items = [] }) => ({
        id: `${id}${PROFROLE_ID_DELIMITER}`,
        text,
        items,
    }));

export const prepareProfRolesOnSave = (items: ProfRoles[], selectedItems: (string | number)[]): string[] =>
    selectedItems
        .reduce((result, id) => {
            const isIdString = typeof id === 'string';
            const categoryIndex = isIdString ? id.indexOf(PROFROLE_ID_DELIMITER) : -1;
            const [realId] = isIdString ? (id.split(PROFROLE_ID_DELIMITER) as [string, string?]) : [id];
            const integerRealId = +realId;

            result.push(
                ...(categoryIndex >= 0
                    ? items
                          .find(({ id: categoryId }) => categoryId === integerRealId)
                          ?.professionalRoles?.map(({ id }) => id) ?? []
                    : [integerRealId])
            );

            return result;
        }, [] as number[])
        .map(String);

export const isBEProfRoles = (data: unknown): data is BEProfRoles =>
    Boolean(
        data &&
            typeof data === 'object' &&
            (data as BEProfRoles).professionalCategories &&
            Array.isArray((data as BEProfRoles).professionalCategories)
    );

export const hasProfRoles = (data: { ['profRoles']?: BEProfRoles }): boolean =>
    Boolean(data && isBEProfRoles(data.profRoles));

const FILTERED_PROFESSIONS_MAP = (profRoles: ProfRoles[], campaignProfessions: string[]) => {
    const idsSet = new Set(campaignProfessions);
    const allProfessions = profRoles.reduce((result, { professionalRoles }) => {
        if (idsSet.size && professionalRoles) {
            professionalRoles.forEach((role) => {
                const stringId = String(role.id);

                if (idsSet.has(stringId)) {
                    result.push(role);
                    idsSet.delete(stringId);
                }
            });
        }

        return result;
    }, [] as ProfRoles[]);

    return [allProfessions[0], allProfessions] as const;
};

export const getSelectedProfessions = (
    profRoles: ProfRoles[],
    campaignProfessions: string[]
): ReturnType<typeof FILTERED_PROFESSIONS_MAP> => FILTERED_PROFESSIONS_MAP(profRoles, campaignProfessions);
